import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { makeAutoObservable } from 'mobx';
import { AccountState } from 'src/domains/players/state/accountState/AccountState';
import { MessageDataType } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/Iframe';
import { AccountModel } from 'src/domains/players/state/accountState/AccountModel';
import { amountViewToValue } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/helpers/amountViewToValue';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { UsersState } from 'src/domains/players/state/UsersState';
import { RealexDepositSteps } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/realexProvider/RealexJourney';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    validateAmountRequire,
    validateMaxDeposit,
    validateMinAmountNew,
} from 'src/domains/players/webview/components/ValidatorsNew';
import { Amount } from 'src_common/common/amount/Amount';
import {
    BillingInfoSuccess,
    billingInfoFieldsValidationDeposit,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/helpers/billingInfoFiledsValidation/realexBillingInfoFieldsValidation';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { Common } from 'src/domains/common/Common';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { RemainingLimitState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/remainingLimit/RemainingLimit.state';
import { DepositYaspaPageType } from 'src/domains/players/webview/components/WithdrawAndDeposit/yaspa/YaspaSegment/YaspaSegment.state';
import { DepositErrorMessagesState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/errorMessages/DepositErrorMessages.state';
import {
    isAccountDepositBlocked,
    refreshAccountBasicDataForDeposit,
    syncAccountDepositBlockingErrors,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/helpers/accountDepositBlocking';

export class RealexTabState {
    public stepsState: RealexDepositSteps;

    public refDepositInput: HTMLInputElement | null;
    public showMoreRingFenceFlag: boolean = false;
    public transactionId: number | undefined = undefined;

    private addedDepositTag = false;

    public readonly depositAmount: FormInputState<string, Amount>;
    public readonly accountHelperMail: string;
    public readonly remainingLimitState: RemainingLimitState;
    private readonly languagesState: LanguagesState;
    private readonly minDepositAmount: Amount;
    private readonly googleTagManager: GoogleTagManagerState;
    private readonly account: AccountState;
    private readonly basicDataModel: BasicDataModel;
    private readonly usersState: UsersState;
    private readonly configComponents: ConfigComponents;
    public readonly errorMessagesState: DepositErrorMessagesState;

    public constructor(
        private readonly common: Common,
        public readonly isSignup: boolean
    ) {
        this.configComponents = ConfigComponents.get(common);
        this.accountHelperMail = this.configComponents.config.accountHelperMail;
        this.minDepositAmount = new Amount(this.configComponents.config.minDepositAmount);
        this.basicDataModel = BasicDataModel.get(common);
        this.languagesState = LanguagesState.get(common);
        this.usersState = UsersState.get(common);
        this.googleTagManager = GoogleTagManagerState.get(common);
        this.remainingLimitState = RemainingLimitState.get(common);
        this.stepsState = new RealexDepositSteps(this.isSignup);
        this.account = AccountState.get(common);
        this.refDepositInput = null;
        this.errorMessagesState = new DepositErrorMessagesState(common, this.languagesState);
        this.depositAmount = FormInputState.new('')
            .map(validateAmountRequire)
            .map(
                validateMinAmountNew(
                    this.minDepositAmount,
                    this.languagesState.getTranslation(
                        'errors.min-deposit',
                        'Minimum deposit amount is {currencySymbol}{minValue}',
                        {
                            currencySymbol: BasicDataModel.get(this.common).moneySymbol,
                            minValue: this.minDepositAmount.value,
                        }
                    )
                )
            )
            .map(
                validateMaxDeposit(
                    () => this.remainingLimitState.remainingLimit ?? null,
                    () => this.basicDataModel.currency,
                    this.languagesState,
                    () => this.basicDataModel.basicDataReady?.depositRestriction,
                    this.accountHelperMail,
                    () => this.remainingLimitState.bindingLimitCalculationType
                )
            );

        this.areMissingFieldsForBillingInfoRealex();

        makeAutoObservable(this);

        void refreshAccountBasicDataForDeposit(this.common);
    }

    public areMissingFieldsForBillingInfoRealex = (): boolean => {
        const basicData = BasicDataModel.get(this.common).basicDataReady ?? null;
        const billingInfo = billingInfoFieldsValidationDeposit(basicData);

        if (billingInfo?.type === 'error') {
            this.errorMessagesState.createErrorMessage('missing-fields', undefined, billingInfo.fieldsRequired);

            return true;
        }

        return false;
    };

    public get depositYaspaPage(): DepositYaspaPageType {
        return {
            isSignup: this.isSignup,
            getAmountInput: () => this.depositAmount,
            handleRedirectToYaspaFrame: (link) => this.stepsState.redirectToIframeYaspa({ link }),
        };
    }

    public get showBalance(): string | undefined {
        const playableBalance = this.usersState.walletData.valueReady?.playableBalance;

        if (playableBalance !== undefined) {
            return this.basicDataModel.money(new Amount(playableBalance));
        }
    }

    /* amount Input start*/
    public onChange = (): void => {
        const formatValue = amountViewToValue(this.depositAmount.value);
        this.depositAmount.setValue(formatValue);
        this.syncDepositBlockingErrors();
    };

    public setAmount = (): void => {
        const amount = parseFloat(this.depositAmount.value);
        if (isNaN(amount) === true || amount === 0) {
            this.depositAmount.setValue('');
            this.syncDepositBlockingErrors();
            return;
        }
        this.depositAmount.setValue(amount.toFixed(2));
        this.syncDepositBlockingErrors();
    };

    public handleAdditionsChange = (amount: Amount): void => {
        this.depositAmount.setValue(amount.value);
        this.depositAmount.setAsVisited();
        this.syncDepositBlockingErrors();
    };

    public onSetInputToEmpty = (): void => {
        this.depositAmount.reset();
    };

    public get hasInputValue(): boolean {
        return this.depositAmount.value !== '';
    }
    /* amount Input end*/

    /* ringFencedFundsFlag start */
    public toggleShowMore = (): void => {
        this.showMoreRingFenceFlag = !this.showMoreRingFenceFlag;
    };

    public get hasRingFencedFunds(): boolean {
        return this.basicDataModel.basicDataReady?.ringFencedFunds ?? false;
    }
    /* ringFencedFundsFlag end*/

    public submitDepositForm = async (): Promise<void> => {
        this.depositAmount.setAsVisited();
        this.syncDepositBlockingErrors();

        if (this.depositAmount.result.value.type === 'error') {
            return;
        }

        if (isAccountDepositBlocked(this.common, this.hasInputValue)) {
            return;
        }

        const amount = new Amount(this.depositAmount.value);
        const oldFormatAmount = ConfigComponents.get(this.common).precision.valueOldFormat(amount);
        const account = this.account.account;

        if (account === null) {
            console.error('submitDepositForm - User is anonymous');
            return;
        }

        const billingInfo = billingInfoFieldsValidationDeposit(this.basicDataModel.basicDataReady);
        if (billingInfo?.type !== 'ok') {
            console.error('submitDepositForm - No basic user data');
            return;
        }

        try {
            if (this.hasRingFencedFunds === false) {
                await this.usersState.onChangeRingFencedFlag();
            }

            if (this.isSignup) {
                this.googleTagManager.depositedMoney(oldFormatAmount, true);
                this.googleTagManager.gtmSignUpStepFour(oldFormatAmount);
            } else {
                this.googleTagManager.depositedMoney(oldFormatAmount, false);
            }

            //for new cards and saved card by iframe
            await this.onSubmitCard(oldFormatAmount, billingInfo.data, account);
        } catch (e) {
            this.stepsState.redirectToFailureView('serverIssue');
            throw e;
        }
    };

    private onSubmitCard = async (
        amount: number,
        billingInfoData: BillingInfoSuccess['data'],
        account: AccountModel
    ): Promise<void> => {
        const response = await account.onSendTopUpAmount({
            requestBody: {
                amount,
                paymentMethod: undefined,
                billingInfo: billingInfoData,
                channel: 'default',
                status: 'initiated',
            },
        });

        if (response.status === '200') {
            const { bodyJson } = response;
            this.transactionId = bodyJson.id;

            const paymentForm = bodyJson.paymentForm ?? false;

            if (bodyJson.status === 'failed') {
                if (bodyJson.reason === 'invalid-data') {
                    this.errorMessagesState.createErrorMessage('invalid-data');
                } else {
                    this.stepsState.redirectToFinalView(this.transactionId);
                }
            } else if (paymentForm === false) {
                throw new Error('Payment form is null');
            } else {
                this.stepsState.redirectToIframeNewCard({
                    formActionUrl: paymentForm.formActionUrl,
                    formData: paymentForm.formData,
                    onMessage: this.onMessage,
                });
            }
        } else {
            const { errors } = response;

            const error = errors[0];

            if (error === undefined) {
                this.stepsState.redirectToFailureView('serverIssue');
                return;
            }

            if (typeof error.code === 'string') {
                this.errorMessagesState.createErrorMessage(error.code, error.debugDetails ?? undefined);
            } else {
                this.stepsState.redirectToFinalView(undefined);
            }
        }
    };

    public onMessage = (data: MessageDataType): void => {
        if (data.type === 'success') {
            this.stepsState.redirectToFinalView(this.transactionId);

            if (this.addedDepositTag === false) {
                this.addedDepositTag = true;
                this.googleTagManager.addDepositTag(this.account.account);
            }
        } else {
            this.stepsState.redirectToFinalView(this.transactionId);
        }
    };

    public hideDepositSuccess = (): void => {
        this.onSetInputToEmpty();
        this.stepsState.redirectToSetMethod();
    };

    private syncDepositBlockingErrors = (): void => {
        syncAccountDepositBlockingErrors(this.common, this.errorMessagesState, this.hasInputValue);
    };

    /* nextButton start (in accounts Tab) */
    public get isButtonDisabled(): boolean {
        if (this.depositAmount.result.value.type === 'error') {
            return true;
        }
        if (isAccountDepositBlocked(this.common, this.hasInputValue)) {
            return true;
        }
        return false;
    }
    /* nextButton end */

    /* used in signUp start */

    public setDepositInputRef = (node: HTMLInputElement | null): void => {
        this.refDepositInput = node;
    };
}
